import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { emptyArray } from 'ui-components/default-props/empty';
import { useEnableAddShipmentItems } from '../../../../../feature-flags/hooks/use-enable-add-shipment-items';
import { useEnableReship } from '../../../../../feature-flags/hooks/use-enable-reship';
import { useFulfillmentPlanIdContext } from '../../../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useCanUnlink } from '../../../../../fulfillment-plans/unlink-button/hooks';
import { useEntitiesFulfillmentPlanProperty } from '../../../../../hooks/entities/selectors/fulfillment-plans';
import { useEntitiesSalesOrdersByFulfillmentPlanId } from '../../../../../hooks/entities/selectors/sales-orders';
import { useCurrentUserId } from '../../../../../hooks/use-current-user-id';
import {
  useDeprecatedLocalStorage,
  generateUserSpecificPersistentKey,
  HAS_DISMISSED_ADDITIONAL_ITEMS_BANNER,
} from '../../../../../hooks/use-local-storage';
import { useOnElementWidthChange } from '../../../../../hooks/use-on-element-width-change';
import { useSalesOrderIdContext } from '../../../../context/sales-order-id-context-provider';
import { useInventoryStockLevels } from '../../../context/inventory-stock-levels-context';
import { ItemImageColumnId, itemImageDataBuilder } from '../../../items/items-table/columns/item-image';
import {
  getItemSalesOrderFromLinkedSalesOrders,
  ItemNameColumnId,
  itemNameDataBuilder,
} from '../../../items/items-table/columns/item-name';
import { ItemQuantityColumnId, itemQuantityDataBuilder } from '../../../items/items-table/columns/item-quantity';
import { ItemStockColumnId, itemStockDataBuilder } from '../../../items/items-table/columns/item-stock';
import { ItemTotalColumnId, itemTotalDataBuilder } from '../../../items/items-table/columns/item-total';
import { itemUnitCostBuilder, ItemUnitCostColumnId } from '../../../items/items-table/columns/item-unit-cost';
import { ShipmentItemsTableEmptyRow } from '../../../items/items-table/empty-row/shipment-items-table-empty-row';
import ItemsTable from '../../../items/items-table/items-table';
import { useHiddenColumnsForTableWidth } from '../../../items/items-table/use-hidden-columns-for-table-width';
import ExpanderSection from '../../../tab-section/expander-section/expander-section';
import { OrderDetailsSectionId } from '../../order-details-expanded-section-provider';
import { AdditionalItemsBanner } from './additional-items-banner/additional-items-banner';
import columns from './columns';
import { Header } from './header/header';
import { RemoveShipmentBanner } from './header/remove-shipment/remove-shipment-banner';
import { getItemInventoryClassName } from './helpers/get-item-inventory-class-name';
import { getItemInventoryStatus, shipmentItemInventoryStatus } from './helpers/get-item-inventory-status';
import { LowStockBanner } from './low-stock-banner';
import messages from './messages';
import { ScanToVerify } from './scan-to-verify';
import { ScanVerificationContextProvider, useScanVerificationContext } from './scan-to-verify/scan-verification-context-provider';
import style from './shipment-items.module.less';

const hiddenColumnsForWidthMap = {
  640: [ItemUnitCostColumnId],
  544: [ItemImageColumnId, ItemStockColumnId],
};

const initialHiddenColumns = [ItemStockColumnId];

const ShipmentItems = () => {
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const items = useEntitiesFulfillmentPlanProperty(fulfillmentPlanId, 'items') || emptyArray;
  const pickBatchExceptionInfo = useEntitiesFulfillmentPlanProperty(fulfillmentPlanId, 'pickBatchExceptionInfo');
  const { inventoryInfo, inventoryStockLevelsMap, shouldShowInventoryInfo, shouldShowInventoryInfoUiTreatments } =
    useInventoryStockLevels();
  const currentSalesOrderId = useSalesOrderIdContext();
  const linkedSalesOrders = useEntitiesSalesOrdersByFulfillmentPlanId(fulfillmentPlanId);
  const canUnlink = useCanUnlink(fulfillmentPlanId, currentSalesOrderId);
  const isReshipEnabled = useEnableReship();
  const isAddShipmentItemsEnabled = useEnableAddShipmentItems();
  const userId = useCurrentUserId();
  const [hasDismissedAdditionalItemsBanner, setIsAdditionalItemsBannerDismissed] = useDeprecatedLocalStorage(
    generateUserSpecificPersistentKey(HAS_DISMISSED_ADDITIONAL_ITEMS_BANNER, userId),
    false,
  );

  const getOtherHiddenColumns = useCallback(() => {
    return shouldShowInventoryInfo ? emptyArray : [ItemStockColumnId];
  }, [shouldShowInventoryInfo]);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, []);

  useOnElementWidthChange(containerRef, setContainerWidth);

  const hiddenColumns = useHiddenColumnsForTableWidth(
    containerWidth,
    hiddenColumnsForWidthMap,
    initialHiddenColumns,
    getOtherHiddenColumns,
  );

  const formattedItems = useMemo(
    () =>
      items.map((item) => {
        const itemInventoryStatus = getItemInventoryStatus({
          item,
          inventoryStockLevelsMap,
          shouldShowInventoryInfo,
        });

        const productExceptions = pickBatchExceptionInfo?.productExceptions || {};
        const productIdKey = item.productId ? String(item.productId) : null;
        const pickingExceptionMessage = productIdKey ? productExceptions[productIdKey] : null;
        const pickingExceptionReporter = pickBatchExceptionInfo?.reporter || null;

        return {
          [ItemNameColumnId]: itemNameDataBuilder(
            item,
            getItemSalesOrderFromLinkedSalesOrders(item, linkedSalesOrders),
            currentSalesOrderId,
          ),
          [ItemImageColumnId]: itemImageDataBuilder(item),
          [ItemUnitCostColumnId]: itemUnitCostBuilder(item),
          [ItemQuantityColumnId]: itemQuantityDataBuilder(item),
          [ItemStockColumnId]: itemStockDataBuilder(inventoryStockLevelsMap, inventoryInfo?.isLoading, item),
          [ItemTotalColumnId]: itemTotalDataBuilder(item),
          // not shown in the table, these values can be used in the table rows.
          // accessed via the `original` object in the table row, e.g. row.original.salesOrderItemId
          fulfillmentPlanId,
          fulfillmentPlanItemId: item.fulfillmentPlanItemId,
          salesOrderItemId: item.salesOrderItemId,
          attributes: item.attributes,
          itemInventoryStatus,
          itemInventoryClassName: getItemInventoryClassName(shouldShowInventoryInfoUiTreatments, itemInventoryStatus),
          shouldShowInventoryInfoUiTreatments,
          pickingExceptionMessage,
          pickingExceptionReporter,
        };
      }),
    [
      items,
      linkedSalesOrders,
      currentSalesOrderId,
      inventoryStockLevelsMap,
      inventoryInfo?.isLoading,
      fulfillmentPlanId,
      shouldShowInventoryInfo,
      shouldShowInventoryInfoUiTreatments,
      pickBatchExceptionInfo,
    ],
  );

  const showLowStockBanner =
    shouldShowInventoryInfoUiTreatments &&
    formattedItems.some((item) => item.itemInventoryStatus === shipmentItemInventoryStatus.hasLowStock);
  const inventoryProviderName = inventoryStockLevelsMap[formattedItems[0]?.fulfillmentPlanItemId]?.providerName;

  const shouldShowAdditionalItemsBanner =
    (isReshipEnabled || isAddShipmentItemsEnabled) &&
    !hasDismissedAdditionalItemsBanner &&
    formattedItems.some((item) => !item.salesOrderItemId);

  const dismissAdditionalItemsBanner = useCallback(() => {
    setIsAdditionalItemsBannerDismissed(true);
  }, [setIsAdditionalItemsBannerDismissed]);

  return (
    <ScanVerificationContextProvider fulfillmentPlanId={fulfillmentPlanId} items={items}>
      <ShipmentItemsContent
        canUnlink={canUnlink}
        containerRef={containerRef}
        dismissAdditionalItemsBanner={dismissAdditionalItemsBanner}
        formattedItems={formattedItems}
        hiddenColumns={hiddenColumns}
        inventoryProviderName={inventoryProviderName}
        shouldShowAdditionalItemsBanner={shouldShowAdditionalItemsBanner}
        showLowStockBanner={showLowStockBanner}
      />
    </ScanVerificationContextProvider>
  );
};

const ShipmentItemsContent = ({
  canUnlink,
  containerRef,
  dismissAdditionalItemsBanner,
  formattedItems,
  hiddenColumns,
  inventoryProviderName,
  shouldShowAdditionalItemsBanner,
  showLowStockBanner,
}) => {
  const { isInVerificationMode } = useScanVerificationContext();

  return (
    <div ref={containerRef}>
      <ExpanderSection
        ariaLabelMessageDescriptor={messages.sectionAriaLabel}
        contentClassName={style['shipment-items-section-content']}
        headerContent={<Header />}
        sectionContent={
          <>
            {isInVerificationMode ? (
              <ScanToVerify />
            ) : (
              <>
                {shouldShowAdditionalItemsBanner ? <AdditionalItemsBanner onDismiss={dismissAdditionalItemsBanner} /> : null}
                {showLowStockBanner ? <LowStockBanner inventoryProviderName={inventoryProviderName} /> : null}
                <ItemsTable
                  columns={columns}
                  emptyRow={<ShipmentItemsTableEmptyRow />}
                  hiddenColumns={hiddenColumns}
                  items={formattedItems}
                />
              </>
            )}
          </>
        }
        sectionId={OrderDetailsSectionId.ShipmentItems}
        subHeaderContent={canUnlink ? <RemoveShipmentBanner /> : null}
      />
    </div>
  );
};

export default ShipmentItems;
